import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, Paper, InputAdornment, IconButton,
  styled, keyframes, Alert, CircularProgress, Link,
} from '@mui/material';
import {
  Lock as LockIcon, MailOutline as MailIcon,
  Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios, { API, setToken, setUser } from '../api';

const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: '#101012',
  border: '1px solid #27272a',
  padding: theme.spacing(4),
  borderRadius: 16,
  boxShadow: '0 30px 60px -20px rgba(0, 0, 0, 0.7)',
  maxWidth: 400,
  width: '100%',
  animation: `${fadeIn} 0.5s ease-out`,
}));

const Field = styled(TextField)({
  '& .MuiOutlinedInput-root': { backgroundColor: '#18181b' },
});

const Login = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const endpoint = mode === 'register' ? '/api/auth/register' : '/api/auth/login';
      const { data } = await axios.post(`${API}${endpoint}`, { email, password });
      setToken(data.token);
      localStorage.setItem('isAuthenticated', 'true');
      setUser(data.user || { email });
      // Comptes non validés → page d'attente
      if (data.user && data.user.status !== 'active') {
        navigate('/pending');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: '#09090b',
      backgroundImage: 'radial-gradient(60% 50% at 50% 0%, rgba(99,102,241,0.12), transparent 70%)',
      p: 2,
    }}>
      <StyledPaper elevation={0}>
        <Box sx={{ textAlign: 'center', mb: 3.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff', mb: 0.5 }}>
            {mode === 'register' ? 'Créer un compte' : 'Connexion'}
          </Typography>
          <Typography sx={{ color: '#a1a1aa', fontSize: '0.9rem' }}>
            {mode === 'register' ? 'Crée ton espace pour gérer tes comptes' : 'Accède à ton tableau de bord'}
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Field
            fullWidth label="Email" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} required margin="normal" autoComplete="email"
            InputProps={{ startAdornment: <InputAdornment position="start"><MailIcon sx={{ color: '#71717a', fontSize: 20 }} /></InputAdornment> }}
          />

          <Field
            fullWidth label="Mot de passe" type={showPassword ? 'text' : 'password'} value={password}
            onChange={(e) => setPassword(e.target.value)} required margin="normal"
            autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
            InputProps={{
              startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#71717a', fontSize: 20 }} /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((s) => !s)} edge="end" sx={{ color: '#71717a' }}>
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            fullWidth type="submit" variant="contained" disabled={loading}
            sx={{ mt: 2.5, py: 1.25, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : (mode === 'register' ? 'Créer le compte' : 'Se connecter')}
          </Button>
        </form>

        <Box sx={{ textAlign: 'center', mt: 2.5 }}>
          <Typography sx={{ fontSize: '0.85rem', color: '#a1a1aa' }}>
            {mode === 'register' ? 'Déjà un compte ? ' : 'Pas encore de compte ? '}
            <Link
              component="button" type="button" underline="hover"
              onClick={() => { setMode(mode === 'register' ? 'login' : 'register'); setError(''); }}
              sx={{ color: '#818cf8', fontWeight: 600 }}
            >
              {mode === 'register' ? 'Se connecter' : 'Créer un compte'}
            </Link>
          </Typography>
        </Box>
      </StyledPaper>
    </Box>
  );
};

export default Login;
